/**
 * Registers the service worker belonging to the CURRENT host — doc 32 §6.3.
 *
 * THE SAFETY PROPERTY IS THE REFUSAL, NOT THE REGISTRATION.
 *
 * A service worker scope is a path, and only one worker may own it. While `pos.` is
 * still `planned`, POS is served from `www`, where `/` is already controlled by
 * `sw.js` (guest receipts, offline fallback) or `chow-sw.js`. Registering
 * `pos-sw.js` there would not "add" a worker — it would REPLACE the incumbent and
 * drop every cache it owned, which is exactly the failure
 * `hooks/usePushNotifications.ts` documents.
 *
 * So `serviceWorkerForHost` returns `null` for any host that is not `live`, and this
 * component does nothing at all in dev, in previews, and on www. A worker may only
 * claim `/` on a host that is genuinely its own. That makes the whole PWA layer inert
 * until DNS lands, and live the moment it does, with no extra switch to remember.
 */

'use client';

import { useEffect } from 'react';

import { platformHostForHostname } from '@/lib/routing/hosts';
import { serviceWorkerForHost } from '@/lib/routing/host-manifests';

export function HostServiceWorker() {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const host = platformHostForHostname(window.location.hostname);
    const swPath = serviceWorkerForHost(host);
    if (!swPath) return;

    let cancelled = false;

    void (async () => {
      try {
        // Reuse whatever already controls this scope rather than stacking a second
        // registration. On a dedicated host the incumbent is our own worker from a
        // previous load, and `register` on the same path is an update, not a replace.
        const existing = await navigator.serviceWorker.getRegistration('/');
        if (cancelled) return;

        if (existing?.active?.scriptURL.endsWith(swPath)) {
          // Already ours. Ask for an update check, cheaply.
          void existing.update().catch(() => {});
          return;
        }

        await navigator.serviceWorker.register(swPath, { scope: '/' });
      } catch (error) {
        // A failed registration must never break the page. A till that will not load
        // because its offline layer could not install is strictly worse than a till
        // with no offline layer.
        console.warn('[pwa] service worker registration failed', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
